import { useRef, useContext } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { FACEDOWN } from 'constants/gameConstants';
import DealerChipContext from 'contexts/DealerChipContext';
import { useRegisterFloatingAnchor } from 'hooks/useFloatingRegistry';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import { SeatCards } from '../SeatWithPlayer/SeatCards';
import { DealerChip, SeatCardPill } from '../components';
import { getSeatDealerOffset } from '../styles';
import { useSeatAnimation, getPortraitModeStyles } from './index';
import { SeatPosition } from 'hooks/useEllipticalPositions';

type PortraitOpponentProps = {
  seat: any;
  seatId: number;
  currentPlayerSeatId: number;
  isFaceCardUp: boolean;
  position: SeatPosition;
  cards: any;
  hasAction: boolean;
  playerAction: string;
  displayAction?: string;
  actionColor: any;
  isWinner: boolean;
  isTurn: boolean;
  cardPosition: any;
  holeCardCount: number;
  isRotating?: boolean;
};

export const PortraitOpponent = ({
  seat,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  position,
  cards,
  hasAction,
  playerAction,
  displayAction,
  actionColor,
  isWinner,
  isTurn,
  cardPosition,
  holeCardCount,
  isRotating = false
}: PortraitOpponentProps) => {
  const game = useTableSelector((table) => table.game);
  const { isAnimating, dealer } = useContext(DealerAnimationContext);
  const seatRef = useRef<HTMLDivElement>(null);
  const turnGlowRef = useRef<HTMLDivElement>(null);
  const opponentGlowRef = useRef<HTMLDivElement>(null);
  const dealerChipRef = useRef<HTMLDivElement>(null);

  const showAllDealers = useShowAllDealers();
  const showDealerChip = showAllDealers || (!!game.dealerVisible && seatId === dealer && !isAnimating);

  useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);

  useSeatAnimation(isWinner, isTurn, true, seatRef, turnGlowRef, opponentGlowRef);
  const { getOpponentWrapperStyle, getOpponentContentStyle } = getPortraitModeStyles(
    position,
    isRotating
  );

  const showCards = game.seatsWithVisibleCards?.includes(seatId);
  const allowShowdownReveal = !isFaceCardUp && showCards;
  const displayCards = isFaceCardUp
    ? cards
    : Array.from({ length: holeCardCount }, (_, index) => {
        const card = cards?.[index];
        return card && card !== '0' && card !== '' ? card : FACEDOWN;
      });

  return (
    <div style={getOpponentWrapperStyle()}>
      <div ref={seatRef} style={getOpponentContentStyle()}>
      {/* Cards above */}
      {showCards && (
        <SeatCards
          seatId={seatId}
          cards={displayCards}
          position={position}
          cardPosition={cardPosition}
          allowShowdownReveal={allowShowdownReveal}
        />
      )}

      {/* Card pill with avatar, name, stack */}
      <SeatCardPill
        seat={seat}
        seatId={seatId}
        currentPlayerSeatId={currentPlayerSeatId}
        isFaceCardUp={isFaceCardUp}
        isPortrait={true}
        hasAction={hasAction}
        playerAction={playerAction}
        displayAction={displayAction}
        actionColor={actionColor}
        isWinner={isWinner}
        cardPosition={cardPosition}
        glowRef={opponentGlowRef}
        seatLeftPct={position?.leftPct}
      />

      {/* Dealer marker + positioning reference for the floating OuterDealerButton.
          Offset is DERIVED from the seat's position (getSeatDealerOffset): inside
          the seat card, hugging the edge that faces the table centre, bottom-
          aligned so it can never meet the hole-card band at the seat's top. */}
      <div ref={dealerChipRef} style={{
        ...getSeatDealerOffset(position, true),
        opacity: showDealerChip ? 1 : 0,
        pointerEvents: showDealerChip ? 'auto' : 'none'
      }}>
        <DealerChip isPortrait />
      </div>
      </div>
    </div>
  );
};
